<template>
  <div class="content-dropbox">
    <v-card
      outlined
      class="pa-4 drop-zone elevation-0"
      :class="{ 'drag-over': isDragging }"
      @dragenter.prevent="handleDragEnter"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <v-row align="center" no-gutters>
        <v-col cols="auto" class="flex-grow-1">
          <div class="file-text">
            <span v-if="files.length === 0" class="placeholder-text">
              Arrastre {{ multiple ? 'los archivos' : 'el archivo' }} aquí
            </span>
            <div v-else>
              <div v-if="!multiple" class="file-name">
                {{ files[0].name }} ({{ formatFileSize(files[0].size) }})
              </div>
              <div v-else class="file-name">
                {{ files.length }} archivo{{ files.length > 1 ? 's' : '' }} seleccionado{{
                  files.length > 1 ? 's' : ''
                }}
                ({{ getTotalSize() }})
              </div>
            </div>
          </div>
          <input
            type="file"
            ref="fileInput"
            style="display: none"
            :accept="acceptedFileTypes"
            :multiple="multiple"
            @change="handleFileSelect"
          />
        </v-col>
        <v-col cols="auto">
          <v-btn
            color="primary"
            dark
            @click.stop="openFileDialog"
            :disabled="isUploading"
            rounded="pill"
            elevation="0"
            class="text-none"
          >
            {{ 'Elergir Archivo' }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- Lista de archivos (solo en modo múltiple) -->
      <v-row v-if="multiple && files.length > 0 && !isUploading" class="mt-2">
        <v-col>
          <v-list dense class="file-list">
            <v-list-item v-for="(file, index) in files" :key="index" class="px-2">
              <template v-slot:prepend>
                <v-icon :icon="getFileIcon(file)" size="small"></v-icon>
              </template>
              <v-list-item-title class="text-body-2">
                {{ file.name }} ({{ formatFileSize(file.size) }})
              </v-list-item-title>
              <template v-slot:append>
                <v-btn
                  icon="mdi-close"
                  size="x-small"
                  variant="text"
                  @click.stop="removeFile(index)"
                ></v-btn>
              </template>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>

      <!-- Barra de progreso -->
      <v-row v-if="isUploading || uploadProgress > 0" class="mt-2">
        <v-col>
          <div v-if="multiple && currentFileIndex < files.length" class="text-caption mb-1">
            Subiendo archivo {{ currentFileIndex + 1 }} de {{ files.length }}
          </div>
          <v-progress-linear
            :model-value="uploadProgress"
            color="primary"
            height="8"
            rounded
          ></v-progress-linear>
          <div class="text-center mt-1 text-caption">{{ uploadProgress }}%</div>
        </v-col>
      </v-row>

      <!-- Mensaje de error -->
      <v-row v-if="errorMessage" class="mt-2">
        <v-col>
          <v-alert type="error" dense text closable @click:close="errorMessage = ''">
            {{ errorMessage }}
          </v-alert>
        </v-col>
      </v-row>
    </v-card>
    <div class="mt-5">
      <v-c-button
        :label="isUploading ? 'Subiendo...' : 'Cargar Archivo'"
        @click="handleSubmit"
        :loading="isUploading"
        :disabled="files.length === 0 || isUploading"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { dropboxService } from '@/core/services/dropbox'
import { defineComponent, ref, computed, type PropType, type Ref } from 'vue'

export default defineComponent({
  name: 'FileUploadComponent',
  emits: ['file-upload'],
  props: {
    typeFile: {
      type: Array as PropType<string[]>,
      default() {
        return [
          'image/jpeg',
          'image/png',
          'image/gif',
          'image/webp',
          'image/svg+xml',
          'video/mp4',
          'video/mpeg',
          'video/quicktime',
          'video/x-msvideo',
          'video/webm',
          'video/x-matroska',
          'application/pdf',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
        ]
      },
    },
    allowedExtensions: {
      type: Array as PropType<string[]>,
      default() {
        return [
          'jpg',
          'jpeg',
          'png',
          'gif',
          'webp',
          'svg',
          'mp4',
          'mov',
          'avi',
          'webm',
          'mkv',
          'mpeg',
          'mpg',
          'pdf',
          'xlsx',
          'xls',
        ]
      },
    },
    maxFileSize: {
      type: Number,
      default: 100 * 1024 * 1024, // 100MB por defecto (aumentado para videos)
    },
    uploadUrl: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    maxFiles: {
      type: Number,
      default: 10,
    },
  },
  setup(props, { emit }) {
    const files: Ref<File[]> = ref([])
    const isDragging: Ref<boolean> = ref(false)
    const dragCounter: Ref<number> = ref(0)
    const fileInput: Ref<HTMLInputElement | null> = ref(null)
    const isUploading: Ref<boolean> = ref(false)
    const uploadProgress: Ref<number> = ref(0)
    const errorMessage: Ref<string> = ref('')
    const currentFileIndex: Ref<number> = ref(0)

    const acceptedFileTypes = computed(() => {
      const mimeTypes = props.typeFile.join(',')
      const extensions = props.allowedExtensions.map((ext) => `.${ext}`).join(',')
      return `${mimeTypes},${extensions}`
    })

    const openFileDialog = (): void => {
      if (!isUploading.value) {
        fileInput.value?.click()
      }
    }

    const validateFile = (selectedFile: File): boolean => {
      errorMessage.value = ''

      if (selectedFile.size > props.maxFileSize) {
        const maxSizeMB = (props.maxFileSize / (1024 * 1024)).toFixed(2)
        errorMessage.value = `El archivo excede el tamaño máximo permitido de ${maxSizeMB}MB`
        return false
      }

      // Validar tipo MIME
      const validMimeType = props.typeFile.includes(selectedFile.type)

      // Validar extensión
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase()
      const validExtension = fileExtension && props.allowedExtensions.includes(fileExtension)

      if (!validMimeType && !validExtension) {
        errorMessage.value = `Tipo de archivo no permitido. Formatos aceptados: ${props.allowedExtensions.join(', ')}`
        return false
      }

      return true
    }

    const handleFileSelect = (e: Event): void => {
      const target = e.target as HTMLInputElement
      const selectedFiles = target.files
      if (selectedFiles && selectedFiles.length > 0) {
        addFiles(Array.from(selectedFiles))
      }
    }

    const handleDragEnter = (e: DragEvent): void => {
      if (!isUploading.value) {
        dragCounter.value++
        isDragging.value = true
      }
    }

    const handleDragOver = (e: DragEvent): void => {
      if (e.dataTransfer && !isUploading.value) {
        e.dataTransfer.dropEffect = 'copy'
      }
    }

    const handleDragLeave = (e: DragEvent): void => {
      dragCounter.value--
      if (dragCounter.value === 0) {
        isDragging.value = false
      }
    }

    const handleDrop = (e: DragEvent): void => {
      isDragging.value = false
      dragCounter.value = 0

      if (isUploading.value) return

      const droppedFiles = e.dataTransfer?.files
      if (droppedFiles && droppedFiles.length > 0) {
        addFiles(Array.from(droppedFiles))
      }
    }

    const addFiles = (newFiles: File[]): void => {
      errorMessage.value = ''
      const validFiles: File[] = []

      for (const file of newFiles) {
        // Verificar límite de archivos
        if (props.multiple && files.value.length + validFiles.length >= props.maxFiles) {
          errorMessage.value = `Máximo ${props.maxFiles} archivos permitidos`
          return
        }

        if (validateFile(file)) {
          // En modo simple, reemplazar el archivo
          if (!props.multiple) {
            files.value = [file]
            return
          }

          // En modo múltiple, agregar si no existe
          const isDuplicate = files.value.some((f) => f.name === file.name && f.size === file.size)

          if (!isDuplicate) {
            validFiles.push(file)
          }
        }
      }

      if (validFiles.length > 0) {
        files.value = [...files.value, ...validFiles]
      }

      uploadProgress.value = 0
    }

    const removeFile = (index: number): void => {
      files.value.splice(index, 1)
    }

    const getTotalSize = (): string => {
      const totalBytes = files.value.reduce((sum, file) => sum + file.size, 0)
      return formatFileSize(totalBytes)
    }

    const getFileIcon = (file: File): string => {
      if (file.type.startsWith('image/')) return 'mdi-image'
      if (file.type.startsWith('video/')) return 'mdi-video'
      if (file.type === 'application/pdf') return 'mdi-file-pdf-box'
      if (file.type.includes('spreadsheet') || file.type.includes('excel')) return 'mdi-file-excel'
      return 'mdi-file'
    }

    const handleSubmit = async (): Promise<void> => {
      if (!props.uploadUrl) {
        return
      }
      if (files.value.length === 0 || isUploading.value) return

      isUploading.value = true
      uploadProgress.value = 0
      errorMessage.value = ''
      currentFileIndex.value = 0

      try {
        if (props.multiple) {
          await dropboxService.uploadBatch(files.value, props.uploadUrl, (fileIndex, progress) => {
            currentFileIndex.value = fileIndex
            uploadProgress.value = progress.percentage
          })
        } else {
          // Subir un solo archivo
          await dropboxService.upload(files.value[0], props.uploadUrl, (progress) => {
            currentFileIndex.value = 1
            uploadProgress.value = progress.percentage
          })
        }
        files.value = []
        uploadProgress.value = 0
        currentFileIndex.value = 0
        if (fileInput.value) {
          fileInput.value.value = ''
        }
      } catch (error) {
        console.error('Error al subir:', error)
        errorMessage.value = error instanceof Error ? error.message : 'Error al subir el archivo'
      } finally {
        isUploading.value = false
      }
    }

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }

    return {
      files,
      isDragging,
      dragCounter,
      fileInput,
      isUploading,
      uploadProgress,
      errorMessage,
      currentFileIndex,
      acceptedFileTypes,
      openFileDialog,
      handleFileSelect,
      handleDragEnter,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handleSubmit,
      removeFile,
      getTotalSize,
      getFileIcon,
      formatFileSize,
    }
  },
})
</script>

<style scoped>
.content-dropbox {
  background-color: #fff;
}

.drop-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.drop-zone.drag-over {
  border-color: #1976d2;
  background-color: #e3f2fd;
  transform: scale(1.02);
}

.file-text {
  padding: 8px 0;
  min-height: 24px;
}

.placeholder-text {
  color: #666;
  font-size: 14px;
}

.file-name {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.file-list {
  background-color: #f5f5f5;
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
}
</style>
